/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment, useState } from 'react';

import { useMutation } from '@apollo/client';
import type { VivaExam_UserFeedbackChoice as VivaExamUserFeedBackChoice } from '__generated__/graphql-types';
import type { VivaExam_ExamResultSummary as VivaExamResultSummaryType } from '__generated__/graphql-types-do-not-use';

import { Button, ContextualHelp, InlineNotification, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import VivaExamSaveUserFeedbackMutation from 'bundles/academic-integrity/components/viva-exam/api/VivaExamSaveUserFeedbackMutation.graphql';
import type {
  VivaExamSaveUserFeedbackMutationMutation as VivaExamSaveUserFeedbackMutationType,
  VivaExamSaveUserFeedbackMutationMutationVariables as VivaExamSaveUserFeedbackMutationVariables,
} from 'bundles/academic-integrity/components/viva-exam/api/__generated__/VivaExamSaveUserFeedbackMutation';
import IntegrityCheckFeedbackStatus from 'bundles/academic-integrity/components/viva-exam/grader-view/IntegrityCheckFeedbackStatus';
import { FlagStatus } from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/lib/constants';

import _t from 'i18n!nls/academic-integrity';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
  `,
  scoreSection: css`
    display: flex;
    align-items: baseline;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-200);
  `,
  summaryText: (isExamIncomplete: boolean) => css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: ${isExamIncomplete ? 'var(--cds-spacing-1000)' : 'var(--cds-spacing-100)'};
  `,
  betaTag: css`
    padding-right: var(--cds-spacing-100);
  `,
  infoText: css`
    margin-top: var(--cds-spacing-300);
  `,
  inlineNotification: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--cds-spacing-1000);
    margin-top: var(--cds-spacing-200);
  `,
  textSection: css`
    position: relative;
    top: var(--cds-spacing-50);
  `,
};

type IntegrityCheckPanelProps = {
  onViewAnalysisClick: () => void;
  vivaExamResultSummary?: VivaExamResultSummaryType;
  onFeedbackSubmitted: () => void;
  hasErrorFromLatestResultSummary?: boolean;
};

const IntegrityCheckPanel = ({
  onViewAnalysisClick,
  vivaExamResultSummary,
  onFeedbackSubmitted,
  hasErrorFromLatestResultSummary = false,
}: IntegrityCheckPanelProps) => {
  const {
    conclusion,
    flagStatus: integrityCheckFlagStatus,
    feedbackChoice: vivaExamFeedBackStatus,
  } = vivaExamResultSummary || {};

  const { overallComment: integrityCheckSummary, overallScore: integrityCheckScore } = conclusion || {};

  const vivaExamFlagRaised = integrityCheckFlagStatus === FlagStatus.INTEGRITY_ISSUE_FOUND;
  const isExamIncomplete = integrityCheckFlagStatus === FlagStatus.INCOMPLETE;
  const [feedback, setFeedback] = useState('');
  const [feedbackChoice, setFeedbackChoice] = useState<VivaExamUserFeedBackChoice | undefined>(undefined);
  const [userFeedbackSubmitted, setUserFeedbackSubmitted] = useState(vivaExamFeedBackStatus != null);

  const [saveUserFeedback] = useMutation<
    VivaExamSaveUserFeedbackMutationType,
    VivaExamSaveUserFeedbackMutationVariables
  >(VivaExamSaveUserFeedbackMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedback(event.target.value);
  };

  const handleSendFeedback = () => {
    if (feedbackChoice && feedback && vivaExamResultSummary) {
      saveUserFeedback({
        variables: {
          examSessionId: vivaExamResultSummary?.id,
          response: feedback,
          choice: feedbackChoice,
        },
      }).then(() => {
        onFeedbackSubmitted();
        setFeedback('');
        setFeedbackChoice(undefined);
        setUserFeedbackSubmitted(true);
      });
    }
  };

  const handleIconClick = (choice: VivaExamUserFeedBackChoice) => {
    setFeedbackChoice(feedbackChoice === choice ? undefined : choice);
  };

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <ContextualHelp
          helpToggleProps={{
            'aria-label': _t('Information about the integrity check'),
          }}
          tooltipProps={{
            closeButtonProps: {
              'aria-label': _t('Close information about the integrity check'),
            },
          }}
          label={
            <Fragment>
              <Typography2 variant="subtitleLarge" component="span" css={styles.betaTag}>
                {_t('Integrity Check')}
              </Typography2>
            </Fragment>
          }
        >
          <Typography2 variant="bodyPrimary" component="div">
            {_t(
              'Integrity Check is designed to enhance academic integrity. After an assignment submission, learners participate in an AI-moderated Q&A session related to their work, providing an opportunity to further demonstrate their grasp of the subject matter. AI analyzes responses and generates a flag if responses are inconsistent or lack depth.'
            )}
            <div css={styles.infoText}>
              {_t(
                'Flags and resolutions aren’t shared with learners. By taking action on a resolution and providing an optional comment, you will help us improve the accuracy of the Integrity Check flagging system.'
              )}
            </div>
          </Typography2>
        </ContextualHelp>
      </div>

      {!isExamIncomplete && !hasErrorFromLatestResultSummary && (
        <IntegrityCheckFeedbackStatus
          vivaExamFlagRaised={vivaExamFlagRaised}
          userFeedbackSubmitted={userFeedbackSubmitted}
          feedbackChoice={feedbackChoice}
          feedback={feedback}
          handleIconClick={handleIconClick}
          handleFeedbackChange={handleFeedbackChange}
          handleSendFeedback={handleSendFeedback}
        />
      )}

      <div css={styles.scoreSection}>
        <Typography2 variant="subtitleMedium" component="span">
          {_t('Integrity Check score:')}
        </Typography2>
        <Typography2 variant="subtitleMedium" component="span">
          {integrityCheckScore !== undefined ? `${integrityCheckScore} / 10` : '-- / 10'}
        </Typography2>
      </div>
      <Typography2
        css={styles.summaryText(isExamIncomplete || hasErrorFromLatestResultSummary)}
        variant="bodyPrimary"
        component="span"
      >
        {integrityCheckSummary ? `${integrityCheckSummary}` : _t('The learner did not complete the Integrity Check. ')}
      </Typography2>
      {!isExamIncomplete && !hasErrorFromLatestResultSummary && (
        <div>
          <InlineNotification
            inlineAction={
              <Button
                icon={<ArrowNextIcon size="medium" />}
                iconPosition="after"
                size="small"
                variant="ghost"
                onClick={onViewAnalysisClick}
              >
                {_t('View analysis')}
              </Button>
            }
            severity="information"
            css={styles.inlineNotification}
          >
            <Typography2 variant="bodyPrimary" component="span" css={styles.textSection}>
              {_t('More details in the AI analysis')}
            </Typography2>
          </InlineNotification>
        </div>
      )}
    </div>
  );
};

export default IntegrityCheckPanel;
